import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const parseUTC = (iso) => {
  if (!iso) return null;
  if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso.replace(' ', 'T') + 'Z');
  return new Date(iso);
};

export default function Debts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientsDebts, setClientsDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/clients/${user?.id}/with-debts`); setClientsDebts(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openPaymentModal = (sale) => {
    setSelectedSale(sale);
    setPaymentMethod('efectivo');
    setShowPaymentModal(true);
  };

  const markPaid = async () => {
    if (!selectedSale) return;
    try {
      await api.put(`/sales/${selectedSale.id}/mark-paid`, { payment_method: paymentMethod });
      setShowPaymentModal(false);
      setSelectedSale(null);
      await new Promise(r => setTimeout(r, 500));
      load();
      alert('Venta marcada como pagada');
    } catch (e) { alert('Error'); }
  };

  if (loading) return <div className="page-full"><div className="spinner" style={{ marginTop: '40vh' }} /></div>;

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" data-testid="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title">Cuenta Corriente</h1>
      </div>
      <div className="content-scroll">
        {/* All-time notice */}
        <div style={{ background: 'rgba(74,158,255,0.07)', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: '12px', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          Se muestran <strong style={{ color: 'var(--text)' }}>todas las cuentas corrientes pendientes</strong>, sin importar en qué fecha fueron registradas.
        </div>
        {clientsDebts.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon" style={{ color: 'var(--accent)' }}>✅</span>
            <p className="empty-text" style={{ color: 'var(--accent)' }}>¡Todos los clientes están al día!</p>
            <p className="empty-subtext">No hay trabajos sin cobrar</p>
          </div>
        ) : (
          <>
            <div className="summary-card">
              <div className="summary-label">Resumen</div>
              <div className="summary-value">{fmt(clientsDebts.reduce((s, c) => s + c.total_debt, 0))}</div>
              <div className="summary-sub">Total adeudado por {clientsDebts.length} cliente(s)</div>
            </div>

            {clientsDebts.map(c => (
              <div key={c.client_id} className="debt-client-card">
                <button className="debt-client-header" onClick={() => setExpanded(expanded === c.client_id ? null : c.client_id)}>
                  <div>
                    <div className="card-row-title">{c.client_name}</div>
                    <div className="card-row-detail">{c.sales_count} venta(s) pendiente(s)</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="debt-amount">{fmt(c.total_debt)}</span>
                    <span>{expanded === c.client_id ? '▲' : '▼'}</span>
                  </div>
                </button>
                {expanded === c.client_id && (
                  <div className="debt-sales-list">
                    {c.sales.map(s => (
                      <div key={s.id} className="debt-sale-item">
                        <div>
                          <div style={{ fontWeight: '600' }}>{s.product_name}</div>
                          <div className="card-row-detail">{parseUTC(s.created_at)?.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div className="accent-text" style={{ fontWeight: '700' }}>{fmt(s.amount)}</div>
                          <button className="pay-btn" data-testid={`pay-btn-${s.id}`} onClick={() => openPaymentModal(s)}>✓ Pagado</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Forma de Pago</h3>
            <p style={{ textAlign: 'center', marginBottom: '16px', color: 'var(--text2)' }}>
              {selectedSale?.product_name} - <span className="accent-text">{fmt(selectedSale?.amount)}</span>
            </p>
            <div className="payment-section">
              <div className="payment-row">
                <button 
                  className={`payment-btn ${paymentMethod === 'efectivo' ? 'active' : ''}`}
                  data-testid="payment-efectivo"
                  onClick={() => setPaymentMethod('efectivo')}
                >
                  💵 Efectivo
                </button>
                <button 
                  className={`payment-btn ${paymentMethod === 'transferencia' ? 'active' : ''}`}
                  data-testid="payment-transferencia"
                  onClick={() => setPaymentMethod('transferencia')}
                >
                  📱 Transferencia
                </button>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancelar</button>
              <button className="btn-primary" data-testid="confirm-payment-btn" onClick={markPaid}>Confirmar Pago</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
